import { useMemo, useState } from 'react'

import { useUserProgress } from '@/shared/hooks/useUserProgress'
import { effectiveFilter, visibleFilterOptions } from '@/shared/lib/list-view'
import { useLibraryStore } from '@/shared/store/useLibraryStore'
import type { Document } from '@/types/document'

import { LIBRARY_FILTERS, type LibraryFilterId } from '../constants/status'

type DocumentWithStatus = Document & {
  _finished: boolean
  _locked: boolean
}

export const useLibraryView = () => {
  const [rawFilter, setFilter] = useState<LibraryFilterId>('all')
  const { documents } = useLibraryStore()
  const { userProgress, journeyWasFinished, missionWasFinished } =
    useUserProgress()

  const finishedSet = useMemo(
    () => new Set(userProgress?.finished_documents ?? []),
    [userProgress?.finished_documents]
  )

  const enriched: DocumentWithStatus[] = useMemo(() => {
    return documents.map(doc => {
      let locked = false
      if (doc.unlock_journey_id && !journeyWasFinished(doc.unlock_journey_id))
        locked = true
      if (doc.unlock_mission_id && !missionWasFinished(doc.unlock_mission_id))
        locked = true

      // Concluído vence bloqueado: sem isso o material cai em "Concluídos" e
      // em "Em breve" ao mesmo tempo (progresso antigo, jornada reaberta).
      const finished = finishedSet.has(doc.id)

      return {
        ...doc,
        _finished: finished,
        _locked: locked && !finished
      }
    })
  }, [documents, finishedSet, journeyWasFinished, missionWasFinished])

  const matches = (doc: DocumentWithStatus, id: LibraryFilterId) => {
    switch (id) {
      case 'available':
        return !doc._finished && !doc._locked
      case 'success':
        return doc._finished
      case 'locked':
        return doc._locked
      default:
        return true
    }
  }

  const visibleFilters = useMemo(
    () =>
      visibleFilterOptions(LIBRARY_FILTERS, id =>
        enriched.some(doc => matches(doc, id))
      ),
    [enriched]
  )

  const filter = effectiveFilter(rawFilter, visibleFilters)

  const filtered = useMemo(() => {
    switch (filter) {
      case 'available':
        return enriched.filter(d => !d._finished && !d._locked)
      case 'success':
        return enriched.filter(d => d._finished)
      case 'locked':
        return enriched.filter(d => d._locked)
      default:
        return enriched
    }
  }, [enriched, filter])

  const grouped = useMemo(() => {
    const available = filtered.filter(d => !d._finished && !d._locked)
    const completed = filtered.filter(d => d._finished)
    const locked = filtered.filter(d => d._locked)
    return { available, completed, locked }
  }, [filtered])

  const stats = useMemo(() => {
    const total = enriched.length
    const completedCount = enriched.filter(d => d._finished).length
    const totalPoints = enriched.reduce((sum, d) => sum + d.point_reward, 0)
    const totalCoins = enriched.reduce((sum, d) => sum + d.coin_reward, 0)
    const percent = total > 0 ? Math.round((completedCount / total) * 100) : 0
    return { total, completedCount, totalPoints, totalCoins, percent }
  }, [enriched])

  const isEmpty = documents.length === 0
  const isFilteredEmpty = !isEmpty && filtered.length === 0

  return {
    filter,
    visibleFilters,
    setFilter,
    grouped,
    stats,
    isEmpty,
    isFilteredEmpty
  }
}
